import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, ArrowRight, Lock, Loader2, Sparkles, CreditCard, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

const PLANS = {
  individuals: [
    { name: "Solo", price: 29, annualPrice: 25, annualTotal: 299, perks: ["250 prospects / mo", "10 outreach drafts / day", "CSV export", "Native send via Resend"], testid: "billing-solo", quota: 250 },
    { name: "Pro", price: 79, annualPrice: 66, annualTotal: 799, highlight: true, perks: ["1,000 prospects / mo", "Unlimited outreach drafting", "CSV upload + bulk personalise", "Bulk Send All per campaign", "Multi-campaign workspace"], testid: "billing-pro", quota: 1000 },
  ],
  business: [
    { name: "Team", price: 199, annualPrice: 166, annualTotal: 1999, perks: ["3,000 prospects / mo", "3 team seats", "Shared campaigns & lead pools", "Custom From identities", "Onboarding session"], testid: "billing-team", quota: 3000 },
    { name: "Scale", price: 499, annualPrice: 416, annualTotal: 4999, highlight: true, perks: ["10,000 prospects / mo", "10 team seats", "Workspace roles & permissions", "Dedicated success manager", "Custom integrations"], testid: "billing-scale", quota: 10000 },
  ],
};

export default function Billing() {
  const { user, refresh } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [sandboxLoading, setSandboxLoading] = useState(false);
  const [interval, setInterval] = useState("month");
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const sandboxCheckout = searchParams.get("sandbox_checkout") === "true";
  const sandboxPlan = searchParams.get("plan");
  const sandboxPortal = searchParams.get("sandbox_portal") === "true";

  // Calculate remaining trial days
  const getTrialDaysLeft = () => {
    if (!user?.trial_ends_at) return 0;
    const end = new Date(user.trial_ends_at);
    const now = new Date();
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const trialDaysLeft = getTrialDaysLeft();

  
  const handlePortalSession = async () => {
    try {
      const { data } = await api.post("/billing/create-portal-session");
      if (data?.url) {
        if (data.url.startsWith("http")) {
          window.location.href = data.url;
        } else {
          navigate(data.url);
        }
      }
    } catch (e) {
      toast.error("Failed to open billing settings");
    }
  };

  const handleSandboxConfirm = async () => {
    setSandboxLoading(true);
    try {
      await api.post("/billing/sandbox-confirm", { plan: sandboxPlan });
      toast.success("Successfully upgraded workspace plan (Sandbox Simulation)");
      await refresh();
      setSearchParams({});
    } catch (e) {
      toast.error("Sandbox simulation failed");
    } finally {
      setSandboxLoading(false);
    }
  };

  const handleSandboxCancel = async () => {
    setSandboxLoading(true);
    try {
      await api.post("/billing/sandbox-cancel");
      toast.success("Successfully canceled subscription (Sandbox Simulation)");
      await refresh();
      setSearchParams({});
    } catch (e) {
      toast.error("Sandbox simulation failed");
    } finally {
      setSandboxLoading(false);
    }
  };


  const handleCheckout = async (planName) => {
    setCheckoutLoading(planName);
    try {
      const { data } = await api.post("/billing/create-checkout-session", {
        plan_name: planName,
        interval: interval
      });
      if (data?.url) {
        if (data.url.startsWith("http")) {
          window.location.href = data.url;
        } else {
          navigate(data.url);
        }
      }
    } catch (e) {
      toast.error("Checkout failed. Please try again or contact support.");
    } finally {
      setCheckoutLoading(null);
    }
  };

  return (
    <div className="px-6 lg:px-8 py-8 max-w-6xl mx-auto space-y-8 text-left">
      <div className="label-mono mb-2">— Billing</div>
      <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-[#0A0A0F]">Billing & subscription</h1>

      {/* SANDBOX CHECKOUT SIMULATOR */}
      {sandboxCheckout && (
        <div className="bg-amber-50 border-2 border-orange-400 rounded-2xl p-6 shadow-soft animate-in fade-in-50 duration-300">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="h-5 w-5 text-orange-600" />
            <div className="font-display text-lg font-bold text-orange-950">Sandbox Checkout Simulator</div>
          </div>
          <p className="text-sm text-orange-900 mb-5 leading-relaxed">
            Stripe keys are not configured. You are redirecting to the checkout simulator. Click below to simulate inputting a mock credit card and subscribing to the <strong className="font-mono text-orange-950 uppercase">{sandboxPlan}</strong> plan.
          </p>
          <div className="flex gap-3">
            <Button onClick={handleSandboxConfirm} disabled={sandboxLoading} className="rounded-lg bg-orange-600 hover:bg-orange-700 text-white font-medium shadow-sm">
              {sandboxLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Simulating payment...</> : <><CreditCard className="h-4 w-4 mr-2" /> Confirm Mock Payment</>}
            </Button>
            <Button variant="outline" onClick={() => setSearchParams({})} className="rounded-lg border-orange-200 text-orange-850 hover:bg-orange-50">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {sandboxPortal && (
        <div className="bg-amber-50 border-2 border-orange-400 rounded-2xl p-6 shadow-soft animate-in fade-in-50 duration-300">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="h-5 w-5 text-orange-600" />
            <div className="font-display text-lg font-bold text-orange-950">Sandbox Customer Portal Simulator</div>
          </div>
          <p className="text-sm text-orange-900 mb-5 leading-relaxed">
            You requested the billing customer portal. Since no Stripe session exists, you can simulate canceling your active plan back to the trial tier.
          </p>
          <div className="flex gap-3">
            <Button onClick={handleSandboxCancel} disabled={sandboxLoading} className="rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium shadow-sm">
              {sandboxLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</> : <><RefreshCw className="h-4 w-4 mr-2" /> Simulate Subscription Cancellation</>}
            </Button>
            <Button variant="outline" onClick={() => setSearchParams({})} className="rounded-lg border-orange-200 text-orange-855 hover:bg-orange-50">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ACTIVE SUBSCRIPTION CARD */}
      <div className="bg-white border border-[#EAEAE3] rounded-2xl p-6 shadow-soft">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="label-mono">Active Plan</div>
            <div className="flex items-center gap-2">
              <h2 className="font-display text-2xl font-black uppercase text-[#0A0A0F] tracking-tight">{user?.plan} Plan</h2>
              <span className={`px-2.5 py-0.5 rounded-full text-xs font-mono font-bold uppercase tracking-wider ${
                user?.subscription_status === "active" ? "bg-green-100 text-green-800" :
                user?.subscription_status === "trialing" ? "bg-blue-100 text-blue-800" :
                "bg-amber-100 text-amber-800"
              }`}>{user?.subscription_status}</span>
            </div>
            {user?.plan === "trial" ? (
              <p className="text-sm text-[#5C5C66]">
                Your 7-day free trial has <strong className="text-[#0A0A0F]">{trialDaysLeft} days remaining</strong> (ends on {user?.trial_ends_at ? new Date(user.trial_ends_at).toLocaleDateString() : 'N/A'}).
              </p>
            ) : (
              <p className="text-sm text-[#5C5C66]">
                You have active self-serve billing. Quota limit set to <strong className="text-[#0A0A0F]">{user?.leads_quota}</strong> prospects.
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {user?.plan !== "trial" && (
              <Button onClick={handlePortalSession} className="rounded-lg bg-white border border-[#EAEAE3] hover:bg-[#F4F4EE] text-[#0A0A0F]">
                Manage Billing Portal
              </Button>
            )}
          </div>
        </div>

        {/* Trial Progress Bar */}
        {user?.plan === "trial" && (
          <div className="mt-6 space-y-2">
            <div className="flex justify-between text-xs font-mono text-[#5C5C66]">
              <span>Trial Progress</span>
              <span>{Math.max(0, 7 - trialDaysLeft)} / 7 Days Used</span>
            </div>
            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#002FA7] rounded-full transition-all duration-500" style={{ width: `${Math.min(100, Math.max(0, ((7 - trialDaysLeft) / 7) * 100))}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* NATIVE PRICING TABLE */}
      <div className="border-t border-[#EAEAE3] pt-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-8">
          <div>
            <h2 className="font-display text-xl font-bold tracking-tight text-[#0A0A0F]">Available Subscription Tiers</h2>
            <p className="text-sm text-[#5C5C66] mt-0.5">Upgrade or modify your workspace capabilities.</p>
          </div>
          
          <div className="flex flex-col items-end gap-2">
            <div className="relative inline-flex items-center bg-white border border-[#EAEAE3] rounded-full p-1 shadow-soft">
              {/* Floating annotation over Annual */}
              {interval === "year" && (
                <div className="absolute -top-6 right-2 text-[10px] font-mono font-bold text-green-600 uppercase tracking-widest whitespace-nowrap">
                  ✨ 2 Months Free
                </div>
              )}
              <button
                onClick={() => setInterval("month")}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all ${interval === "month" ? "bg-[#0A0A0F] text-white shadow-soft" : "text-[#5C5C66] hover:text-[#0A0A0F]"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setInterval("year")}
                className={`px-5 py-1.5 rounded-full text-sm font-medium transition-all relative ${interval === "year" ? "bg-[#0A0A0F] text-white shadow-soft" : "text-[#5C5C66] hover:text-[#0A0A0F]"}`}
              >
                Annual
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...PLANS.individuals, ...PLANS.business].map((p) => {
            const isHighlight = p.highlight;
            const price = interval === "year" ? p.annualPrice : p.price;
            
            return (
              <div key={p.name} className={`relative flex flex-col rounded-2xl p-6 transition-all duration-300 ${isHighlight ? "bg-[#0A0A0F] text-white border border-[#0A0A0F] shadow-soft-lg" : "bg-white border border-[#EAEAE3] shadow-soft"}`}>
                {isHighlight && (
                  <span className="absolute -top-3 right-6 bg-[#FF5722] text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-md">Most popular</span>
                )}
                <div className={`label-mono mb-2 ${isHighlight ? "!text-white/60" : ""}`}>{p.name}</div>
                <div className="flex items-baseline gap-1.5 mb-2">
                  <span className="font-display text-4xl font-black tracking-tighter">€{price}</span>
                  <span className={`${isHighlight ? "text-white/50" : "text-[#9A9AA3]"} text-sm`}>/ mo</span>
                </div>
                {interval === "year" && (
                  <div className={`text-xs mb-6 ${isHighlight ? "text-white/60" : "text-[#5C5C66]"}`}>
                    Billed €{p.annualTotal} annually
                  </div>
                )}
                {interval === "month" && (
                  <div className={`text-xs mb-6 opacity-0`}>Spacer</div>
                )}
                
                <Button 
                  onClick={() => handleCheckout(p.name)}
                  disabled={checkoutLoading === p.name}
                  className={`w-full rounded-lg mb-6 ${isHighlight ? "bg-white text-[#0A0A0F] hover:bg-white/90" : "bg-[#0A0A0F] text-white hover:bg-[#14141B]"}`}
                >
                  {checkoutLoading === p.name ? <Loader2 className="h-4 w-4 animate-spin" /> : "Subscribe"}
                </Button>
                
                <ul className="space-y-3 mt-auto flex-1">
                  {p.perks.map((perk, i) => (
                    <li key={i} className={`text-sm flex items-start gap-2.5 ${isHighlight ? "text-white/85" : "text-[#0A0A0F]"}`}>
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${isHighlight ? "text-[#FF5722]" : "text-[#002FA7]"}`} />
                      <span className="leading-snug">{perk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
